import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiEdit2 } from 'react-icons/fi';
import { putJoiningDate } from '../api/joiningDateApi';

/** ISO yyyy-mm-dd ➜ "12 Mar 2024" without dragging in a date library. */
function formatDisplayDate(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  const [, y, mo, d] = m;
  // Build at noon UTC so the local tz can't roll us into the previous day.
  const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), 12));
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Joining-date section for the employee profile (Performance tab).
 * - Anyone who can open the profile sees the value (it's not confidential).
 * - HR is the only role allowed to update / clear it.
 */
export default function JoiningDateHrSection({ employeeUserId, joiningDate, canEdit }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(joiningDate || '');

  // If the parent's profile data refetches and brings a new joining_date,
  // sync the draft so an open editor reflects the latest value (when not actively editing).
  useEffect(() => {
    if (!editing) setDraft(joiningDate || '');
  }, [joiningDate, editing]);

  // Switching profiles must reset edit state.
  useEffect(() => {
    setEditing(false);
    setDraft(joiningDate || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeUserId]);

  const saveMutation = useMutation({
    mutationFn: (date) => putJoiningDate(employeeUserId, date),
    onSuccess: (_, date) => {
      toast.success(date == null || date === '' ? 'Joining date cleared' : 'Joining date saved');
      qc.invalidateQueries({ queryKey: ['employee-profile-modal', employeeUserId] });
      setEditing(false);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Save failed'),
  });

  const display = formatDisplayDate(joiningDate);
  const hasValue = !!joiningDate;

  return (
    <div
      style={{
        marginTop: '1.5rem',
        paddingTop: '1.5rem',
        borderTop: '1px solid #e2e8f0',
      }}
    >
      <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
        Joining date
      </h4>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        {hasValue ? (
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '1rem',
              fontWeight: 700,
              color: '#0f172a',
              minWidth: 120,
            }}
          >
            {display}
          </span>
        ) : (
          <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>
            Not set
          </span>
        )}
        {canEdit && !editing ? (
          <button
            type="button"
            onClick={() => {
              setDraft(joiningDate || '');
              setEditing(true);
            }}
            title={hasValue ? 'Edit joining date' : 'Set joining date'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '0.35rem 0.65rem',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#334155',
            }}
          >
            <FiEdit2 size={14} />
            {hasValue ? 'Edit' : 'Set date'}
          </button>
        ) : null}
      </div>

      {canEdit && editing ? (
        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>
            Joining date
          </label>
          <input
            type="date"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            style={{
              padding: '0.5rem 0.65rem',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: '0.9rem',
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => {
                const t = (draft || '').trim();
                if (t === '') {
                  saveMutation.mutate(null);
                  return;
                }
                if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) {
                  toast.error('Pick a valid date (YYYY-MM-DD).');
                  return;
                }
                saveMutation.mutate(t);
              }}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 8,
                border: 'none',
                background: '#0f172a',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: saveMutation.isPending ? 'wait' : 'pointer',
              }}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              disabled={saveMutation.isPending || !hasValue}
              onClick={() => {
                if (window.confirm('Clear the joining date for this employee?')) {
                  saveMutation.mutate(null);
                }
              }}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: saveMutation.isPending || !hasValue ? 'not-allowed' : 'pointer',
                opacity: !hasValue ? 0.5 : 1,
              }}
            >
              Clear
            </button>
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => {
                setEditing(false);
                setDraft(joiningDate || '');
              }}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                background: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: '#475569',
                cursor: saveMutation.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
